import { useState, useRef, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useQueryClient } from '@tanstack/react-query';
import { CalendarIcon, ChevronLeftIcon, ChevronRightIcon, ArrowPathIcon, ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import { clsx } from 'clsx';
import { useInventoryDataStore, useUIStore } from '@/stores';
import { useForceInventoryCollection } from '@/hooks';
import { queryKeys } from '@/services/query-keys';
import { InventoryState } from '@/types';
import { getDefaultStateId, getDailySnapshotState } from '@/utils/inventory/intraday-state-utils';
import { toUtcDateKey } from '@/utils/date-key';
import { formatDateTimeUtc, formatDateTimeLocal } from '@/utils/format';

interface DateTimePickerProps {
  value: number;
  states: InventoryState[];
  onChange: (stateId: number) => void;
  disabled?: boolean;
  onShowRefresh?: () => void;
}

const RATE_SOURCE_ENTRIES: ReadonlyArray<{ key: keyof InventoryState; label: string }> = [
  { key: 'rates_file_name', label: 'Rates' },
  { key: 'velocity_positions_file_name', label: 'Velocity positions' },
  { key: 'cimb_positions_file_name', label: 'CIMB positions' },
  { key: 'velocity_lending_report_file_name', label: 'Velocity lending report' },
  { key: 'cimb_lending_report_file_name', label: 'CIMB lending report' },
  { key: 'marex_lending_report_file_name', label: 'Marex lending report' },
] as const;

export const DateTimePicker: React.FC<DateTimePickerProps> = ({
  value,
  states,
  onChange,
  disabled = false,
  onShowRefresh,
}) => {
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(false);
  // Seed the visible month from UTC today so the grid matches the UTC day-keys
  // (LR-485); navigation then works purely on these calendar integers.
  const [currentMonth, setCurrentMonth] = useState(() => {
    const now = new Date();
    return new Date(now.getUTCFullYear(), now.getUTCMonth(), 1);
  });
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, right: 0 });
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [refreshProgress, setRefreshProgress] = useState(0);
  const refreshIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const isRefreshingRef = useRef(isRefreshing);
  const refreshProgressRef = useRef(refreshProgress);
  isRefreshingRef.current = isRefreshing;
  refreshProgressRef.current = refreshProgress;
  const setRefreshProgressStore = useInventoryDataStore((state) => state.setRefreshProgress);
  const setIsRefreshingStore = useInventoryDataStore((state) => state.setIsRefreshing);
  const setRefreshIntervalId = useInventoryDataStore((state) => state.setRefreshIntervalId);
  const startRefreshStore = useInventoryDataStore((state) => state.startRefresh);
  const clearRefreshState = useInventoryDataStore((state) => state.clearRefreshState);
  const isForceCollecting = useInventoryDataStore((state) => state.isForceCollecting);
  const setDateTimePickerOpen = useUIStore((state) => state.setDateTimePickerOpen);
  const { mutate: forceCollect, isPending: isForceCollectingLoading } = useForceInventoryCollection({
    onNewDataDetected: (newStateId: number) => {
      onChange(newStateId);
    },
  });

  useEffect(() => {
    setDateTimePickerOpen(isOpen);
  }, [isOpen, setDateTimePickerOpen]);
  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY + 8,
        right: window.innerWidth - rect.right + window.scrollX,
      });
    }
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current && 
        !dropdownRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        handleClose();
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- handleClose is a non-memoized closure; including it would re-register the listener every render. Effect only needs to (de)register when isOpen flips.
  }, [isOpen]);

  useEffect(() => {
    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    };
  }, []);

  const selectedState = useMemo(() => {
    return states.find(s => s.id === value) || states[0];
  }, [value, states]);
  const getFileName = (filePath?: string): string => {
    if (!filePath) return '';
    const lastSlashIndex = filePath.lastIndexOf('/');
    return lastSlashIndex >= 0 ? filePath.substring(lastSlashIndex + 1) : filePath;
  };
  const rateSourceEntries = useMemo(() => {
    if (!selectedState) return [];
    return RATE_SOURCE_ENTRIES.map(({ key, label }) => ({
      label,
      fileName: getFileName(selectedState[key] as string | undefined),
    }));
  }, [selectedState]);
  const hasAnyRateSource = rateSourceEntries.some((e) => e.fileName);
  const [rateSourceExpanded, setRateSourceExpanded] = useState(false);

  const statesByDate = useMemo(() => {
    const grouped = new Map<string, InventoryState[]>();
    states.forEach(state => {
      // Bucket by UTC day: snapshots are stamped in UTC and the evening snapshot
      // runs at 22:00 UTC, so local bucketing files it under the next day for
      // viewers east of UTC (LR-485).
      const dateKey = toUtcDateKey(new Date(state.created_at));
      if (!grouped.has(dateKey)) {
        grouped.set(dateKey, []);
      }
      grouped.get(dateKey)!.push(state);
    });
    return grouped;
  }, [states]);

  const availableDates = useMemo(() => {
    return Array.from(statesByDate.keys()).sort();
  }, [statesByDate]);

  const latestDate = useMemo(() => {
    if (availableDates.length === 0) return null;
    return availableDates[availableDates.length - 1];
  }, [availableDates]);

  const [selectedDate, setSelectedDate] = useState<string>(() => {
    if (selectedState) {
      return toUtcDateKey(new Date(selectedState.created_at));
    }
    return latestDate || '';
  });

  useEffect(() => {
    if (selectedState) {
      setSelectedDate(toUtcDateKey(new Date(selectedState.created_at)));
    }
  }, [selectedState]);

  const todayKey = useMemo(() => {
    return toUtcDateKey(new Date());
  }, []);

  const calendarDays = useMemo(() => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startingDayOfWeek = firstDay.getDay();
    const daysInMonth = lastDay.getDate();
    const days: Array<{
      date: number;
      dateKey: string;
      isCurrentMonth: boolean;
      isAvailable: boolean;
      isSelected: boolean;
      isToday: boolean;
      isPast: boolean;
    }> = [];
    const prevMonthLastDay = new Date(year, month, 0).getDate();
    for (let i = startingDayOfWeek - 1; i >= 0; i--) {
      const date = prevMonthLastDay - i;
      const prevMonth = month === 0 ? 11 : month - 1;
      const prevYear = month === 0 ? year - 1 : year;
      const dateKey = `${prevYear}-${String(prevMonth + 1).padStart(2, '0')}-${String(date).padStart(2, '0')}`;
      const isPast = new Date(dateKey) < new Date(todayKey);
      days.push({
        date,
        dateKey,
        isCurrentMonth: false,
        isAvailable: availableDates.includes(dateKey),
        isSelected: dateKey === selectedDate,
        isToday: dateKey === todayKey,
        isPast,
      });
    }
    for (let date = 1; date <= daysInMonth; date++) {
      const dateKey = `${year}-${String(month + 1).padStart(2, '0')}-${String(date).padStart(2, '0')}`;
      const isPast = new Date(dateKey) < new Date(todayKey);
      days.push({
        date,
        dateKey,
        isCurrentMonth: true,
        isAvailable: availableDates.includes(dateKey),
        isSelected: dateKey === selectedDate,
        isToday: dateKey === todayKey,
        isPast,
      });
    }
    const remainingDays = 42 - days.length;
    for (let date = 1; date <= remainingDays; date++) {
      const nextMonth = month === 11 ? 0 : month + 1;
      const nextYear = month === 11 ? year + 1 : year;
      const dateKey = `${nextYear}-${String(nextMonth + 1).padStart(2, '0')}-${String(date).padStart(2, '0')}`;
      const isPast = new Date(dateKey) < new Date(todayKey);
      days.push({
        date,
        dateKey,
        isCurrentMonth: false,
        isAvailable: availableDates.includes(dateKey),
        isSelected: dateKey === selectedDate,
        isToday: dateKey === todayKey,
        isPast,
      });
    }
    return days;
  }, [currentMonth, availableDates, selectedDate, todayKey]);

  const handleClose = () => {
    if (isRefreshingRef.current && onShowRefresh) {
      setRefreshProgressStore(refreshProgressRef.current);
      setIsRefreshingStore(true);
      setRefreshIntervalId(refreshIntervalRef.current);
      onShowRefresh();
    }
    setIsOpen(false);
  };

  const handleRefresh = () => {
    setIsRefreshing(true);
    setRefreshProgress(0);
    startRefreshStore();
    const duration = 60000;
    const interval = 100;
    const steps = duration / interval;
    let currentStep = 0;
    refreshIntervalRef.current = setInterval(() => {
      currentStep++;
      const progress = (currentStep / steps) * 100;
      setRefreshProgress(progress);
      setRefreshProgressStore(progress);
      if (currentStep >= steps) {
        if (refreshIntervalRef.current) {
          clearInterval(refreshIntervalRef.current);
          refreshIntervalRef.current = null;
        }
        setRefreshIntervalId(null);
        clearRefreshState();
        queryClient.invalidateQueries({ queryKey: [...queryKeys.inventory.all] });
      }
    }, interval);
    setRefreshIntervalId(refreshIntervalRef.current);
  };

  const handleDateSelect = (dateKey: string, isAvailable: boolean) => {
    if (!isAvailable) return;
    setSelectedDate(dateKey);
    const statesForDate = statesByDate.get(dateKey) || [];
    if (statesForDate.length > 0) {
      // The day's anchor = its close, falling back to its open: evening (EOD) →
      // morning. This applies to the current day too — once its evening snapshot is
      // collected it becomes the anchor, just like a completed day (LR-503). The
      // freshest snapshot of any type is a separate concept (the "Latest" button).
      // Fall back to the newest state if the day has neither (defensive) —
      // statesForDate is newest-first, so [0].
      const snapshot = getDailySnapshotState(statesForDate) ?? statesForDate[0];
      onChange(snapshot.id);
      handleClose();
    }
  };

  const displayText = useMemo(() => {
    if (!selectedState) return 'Select snapshot time...';
    // Show the snapshot's UTC timestamp with an explicit "UTC" suffix so the time is
    // unambiguous (LR-485) — the same representation for every snapshot, newest or
    // historical. The "Latest" button below jumps to the newest; the field itself
    // just states which snapshot is loaded (LR-503).
    return formatDateTimeUtc(selectedState.created_at);
  }, [selectedState]);

  // Native-title tooltip mapping the canonical UTC snapshot time to the viewer's
  // local clock on hover (LR-485). A title attribute is layout-free, so it never
  // crowds the field — same pattern as the "Data sources" file names below.
  const localTimeTitle = useMemo(
    () => (selectedState ? `Local time: ${formatDateTimeLocal(selectedState.created_at)}` : undefined),
    [selectedState],
  );

  const canGoToNextMonth = useMemo(() => {
    if (!latestDate) return false;
    // latestDate is a UTC 'YYYY-MM-DD' key — parse its parts directly rather than
    // via `new Date(str)`, whose local-tz reading could skew the month (LR-485).
    const [latestYear, latestMonth] = latestDate.split('-').map(Number);
    const nextMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1);
    return nextMonth <= new Date(latestYear, latestMonth - 1, 1);
  }, [currentMonth, latestDate]);

  const goToPreviousMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1));
  };

  const goToNextMonth = () => {
    if (canGoToNextMonth) {
      setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1));
    }
  };

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        title={localTimeTitle}
        className={clsx(
          'w-full flex items-center justify-between px-3 py-2 text-sm border border-gray-300 rounded-md bg-white',
          'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500',
          'disabled:bg-gray-100 disabled:cursor-not-allowed',
          !disabled && 'hover:border-gray-400 cursor-pointer'
        )}
      >
        <span className={clsx(
          'text-left flex-1',
          selectedState ? 'text-gray-900' : 'text-gray-400'
        )}>
          {displayText}
        </span>
        <CalendarIcon className="h-5 w-5 text-gray-400 ml-2 flex-shrink-0" />
      </button>
      {isOpen && createPortal(
        <div 
          ref={dropdownRef}
          className="fixed z-[9999] w-[320px] bg-white rounded-lg shadow-xl border border-gray-200 p-4"
          style={{
            top: `${dropdownPosition.top}px`,
            right: `${dropdownPosition.right}px`,
          }}
        >
          <div>
              <div className="flex items-center justify-between mb-4">
                <button
                  type="button"
                  onClick={goToPreviousMonth}
                  className="p-1 hover:bg-gray-100 rounded"
                >
                  <ChevronLeftIcon className="h-5 w-5 text-gray-600" />
                </button>
                <h3 className="text-sm font-semibold text-gray-900">
                  {currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                </h3>
                <button
                  type="button"
                  onClick={goToNextMonth}
                  disabled={!canGoToNextMonth}
                  className={clsx(
                    'p-1 rounded',
                    canGoToNextMonth 
                      ? 'hover:bg-gray-100 cursor-pointer' 
                      : 'cursor-not-allowed opacity-30'
                  )}
                >
                  <ChevronRightIcon className={clsx(
                    'h-5 w-5',
                    canGoToNextMonth ? 'text-gray-600' : 'text-gray-400'
                  )} />
                </button>
              </div>
              <div className="grid grid-cols-7 gap-1 mb-2">
                {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, index) => (
                  <div key={index} className="text-center text-xs font-medium text-gray-500 py-1">
                    {day}
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-7 gap-1">
                {calendarDays.map((day, index) => (
                  <button
                    key={index}
                    type="button"
                    onClick={() => handleDateSelect(day.dateKey, day.isAvailable)}
                    disabled={!day.isAvailable}
                    className={clsx(
                      'text-center text-sm py-1.5 rounded transition-colors font-medium',
                      day.isSelected && day.isAvailable && 'bg-blue-600 text-white hover:bg-blue-700 font-semibold',
                      !day.isSelected && day.isAvailable && 'text-gray-900 hover:bg-gray-100 cursor-pointer font-semibold',
                      !day.isAvailable && day.isPast && day.isCurrentMonth && 'text-gray-600 cursor-not-allowed',
                      !day.isAvailable && !day.isPast && day.isCurrentMonth && 'text-gray-300 cursor-not-allowed',
                      !day.isCurrentMonth && 'text-gray-300'
                    )}
                  >
                    {day.date}
                  </button>
                ))}
              </div>
            </div>
            {onShowRefresh && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                {!isRefreshing ? (
                  <button
                    type="button"
                    onClick={handleRefresh}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded transition-colors"
                  >
                    <ArrowPathIcon className="h-4 w-4" />
                    Refresh Data
                  </button>
                ) : (
                  <div className="space-y-2">
                    <div className="flex items-center justify-center gap-2 text-sm text-gray-700">
                      <ArrowPathIcon className="h-4 w-4 animate-spin text-blue-600" />
                      <span className="font-medium">Loading... {Math.round(refreshProgress)}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                      <div 
                        className="bg-blue-600 h-2 transition-all duration-100 rounded-full"
                        style={{ width: `${refreshProgress}%` }}
                      />
                    </div>
                    <p className="text-xs text-center text-gray-500">
                      Page will reload after completion
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Real Time Button */}
            <div className="mt-4 pt-4 border-t border-gray-200">
              {hasAnyRateSource && (
                <div className="mb-2">
                  <button
                    type="button"
                    onClick={() => setRateSourceExpanded((prev) => !prev)}
                    className="flex items-center justify-center gap-1 w-full text-xs text-gray-500 hover:text-gray-700 py-1 rounded focus:outline-none"
                    aria-expanded={rateSourceExpanded}
                  >
                    Data sources
                    {rateSourceExpanded ? (
                      <ChevronUpIcon className="h-3.5 w-3.5 flex-shrink-0" aria-hidden />
                    ) : (
                      <ChevronDownIcon className="h-3.5 w-3.5 flex-shrink-0" aria-hidden />
                    )}
                  </button>
                  {rateSourceExpanded && (
                    <ul className="mt-1 text-xs text-gray-500 space-y-1 list-none pl-0">
                      {rateSourceEntries.map(({ label, fileName }) => (
                        <li key={label} className="truncate" title={fileName || undefined}>
                          <span className="font-medium text-gray-600">{label}:</span> {fileName || '-'}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => {
                    // Jump to the newest snapshot, whatever its type — the live
                    // intraday during the day, or the frozen evening EOD snapshot
                    // after the close (getDefaultStateId, LR-503).
                    const latestStateId = getDefaultStateId(states);
                    if (latestStateId !== undefined) {
                      onChange(latestStateId);
                      handleClose();
                    }
                  }}
                  disabled={!states.length}
                  className="flex-1 flex items-center justify-center px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded transition-colors disabled:text-gray-400 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                >
                  Latest
                </button>
                <button
                  type="button"
                  onClick={() => {
                    forceCollect();
                  }}
                  disabled={isForceCollecting || isForceCollectingLoading}
                  className={clsx(
                    'flex-1 flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium rounded transition-colors whitespace-nowrap',
                    isForceCollecting || isForceCollectingLoading
                      ? 'text-gray-400 cursor-not-allowed bg-gray-50'
                      : 'text-blue-600 hover:text-blue-700 hover:bg-blue-50'
                  )}
                >
                  {isForceCollecting || isForceCollectingLoading ? (
                    <>
                      <ArrowPathIcon className="h-4 w-4 animate-spin" />
                      Refreshing...
                    </>
                  ) : (
                    <>
                      <ArrowPathIcon className="h-4 w-4" />
                      Refresh data
                    </>
                  )}
                </button>
              </div>
            </div>
        </div>,
        document.body
      )}
      <style>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 3px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 3px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #94a3b8;
        }
      `}</style>
    </div>
  );
};

